/**
 * Smart Blocks engine — barrel.
 *
 * Public surface:
 *   - resolveSmartToken(block, smartBlock, context) → ContentBlock[]
 *   - expandSmartTokens(blocks, catalog, context) → ContentBlock[]  (bulk)
 *   - RESOLVERS, RESOLVER_KIND_IDS               (registry)
 *   - BUILT_IN_SMART_BLOCKS, isBuiltInSmartBlockId, findSmartBlockInCatalog
 *   - buildProjectContext, buildSampleContext   (context construction)
 *   - renderTemplate                              (template engine for previews)
 */

import type { ContentBlock, ProjectContext, SmartBlock } from '../../shared/types';
import { findSmartBlockInCatalog } from './built-in-catalog';
import { resolveSmartToken } from './resolvers';

export { resolveSmartToken, RESOLVERS, RESOLVER_KIND_IDS } from './resolvers';
export {
  BUILT_IN_SMART_BLOCKS,
  isBuiltInSmartBlockId,
  builtInIdFor,
  findSmartBlockInCatalog,
} from './built-in-catalog';
export { buildProjectContext, buildSampleContext } from './context-builder';
export type { ContextInputs } from './context-builder';
export { renderTemplate } from './template-engine';
export type { TemplateRenderResult } from './template-engine';

/**
 * Walk a ContentBlock[] and expand every `smart-token` block in place,
 * preserving the order of non-token blocks. Resolved blocks inherit the
 * smart-token's level / variantId / excludedFromVariants stamps.
 *
 * Used by the project-spec compile pipeline and the Word export path.
 */
export function expandSmartTokens(
  blocks: ContentBlock[],
  firmCatalog: SmartBlock[],
  context: ProjectContext
): ContentBlock[] {
  const out: ContentBlock[] = [];
  for (const block of blocks) {
    if (block.blockType !== 'smart-token') {
      out.push(block);
      continue;
    }
    const sb = findSmartBlockInCatalog(block.smartBlockId, firmCatalog);
    const resolved = resolveSmartToken(block, sb, context);
    out.push(...resolved);
  }
  return out;
}
